/**
 * «Lo que queda sin decidir se da por aceptado», con un clic y su confirmación (14-oct-2026).
 *
 * Va debajo del aviso de cambios sin decidir (Reescritura y Corrección). Lo hace Word (\`office.aceptarLosPendientesYGuardar\`):
 * es «Aceptar todos los cambios» en el documento ABIERTO y guardarlo. Por eso solo se enseña cuando los cambios que
 * cuenta el motor son los del documento abierto. Al acabar se apunta en el diario del libro y se vuelve a mirar.
 */
import { useState } from "react";
import { COMPANION } from "./companion";
import { aceptarLosPendientesYGuardar } from "./office";
import { fraseDeAceptarPendientes, rotuloDeAceptarPendientes, preguntaDeAceptarPendientes, FRASE_SIN_API_PARA_ACEPTAR, fraseDeAceptados, fraseDeAceptadosSinGuardar } from "./reescrituraLogica";

export type PuenteDeAceptar = {
  aceptar: () => Promise<{ ok: true; aceptados: number } | { ok: false; motivo: "sin_api" | "fallo" | "sin_guardar"; aceptados?: number; error?: string }>;
  apuntar: (dir: string, doc: string, n: number) => Promise<void>;
};
const PUENTE: PuenteDeAceptar = {
  aceptar: aceptarLosPendientesYGuardar,
  apuntar: async (dir, doc, n) => {
    await fetch(`${COMPANION}/pendientes/aceptados`, { method: "POST", headers: { "x-hi-cliente": "word", "content-type": "application/json" }, body: JSON.stringify({ dir, doc, n }) }).catch(() => null);
  },
};

// Sus estilos van con ella: se pone en pestañas con estilos distintos (Reescritura, Corrección).
export const CSS_DE_ACEPTAR = `
.ap-nota { font-size:13px; line-height:1.45; color:var(--hi-tinta); border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; margin-top:8px; overflow-wrap:anywhere; }
.ap-nota p { margin:0 0 6px; }
.ap-botones { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.ap-boton { font:inherit; font-size:13px; font-weight:700; padding:9px 12px; border-radius:8px; border:1px solid var(--hi-humano); background:transparent; color:var(--hi-humano); cursor:pointer; }
.ap-boton:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.ap-boton:disabled { opacity:.5; cursor:not-allowed; }
.ap-principal { background:var(--hi-oficio); border-color:var(--hi-oficio); color:var(--hi-sobre-acento); font-weight:800; }
.ap-mal { color:var(--hi-mal); font-size:12px; }
.ap-bien { color:var(--hi-bien, var(--hi-tinta)); font-size:12px; }
`;

export default function AceptarPendientes({ n, dir, doc, alTerminar, puente = PUENTE }: { n: number; dir: string; doc: string; alTerminar: () => void; puente?: PuenteDeAceptar }) {
  const [paso, setPaso] = useState<"nada" | "preguntando" | "aceptando">("nada");
  const [dicho, setDicho] = useState<{ tono: "bien" | "mal"; texto: string } | null>(null);
  if (!(n > 0)) return null;
  const aceptar = async () => {
    setPaso("aceptando"); setDicho(null);
    const r = await puente.aceptar();
    if (r.ok) {
      await puente.apuntar(dir, doc, r.aceptados);
      setDicho({ tono: "bien", texto: fraseDeAceptados(r.aceptados) });
      setPaso("nada");
      alTerminar();
      return;
    }
    if (r.motivo === "sin_guardar") {
      // Están aceptados en el documento: se apunta igual, y se dice que falta guardar.
      await puente.apuntar(dir, doc, r.aceptados ?? 0);
      setDicho({ tono: "mal", texto: fraseDeAceptadosSinGuardar(r.aceptados ?? 0) });
      setPaso("nada");
      return;
    }
    setDicho({ tono: "mal", texto: r.motivo === "sin_api" ? FRASE_SIN_API_PARA_ACEPTAR : `No he podido: ${r.error ?? "Word no ha contestado"}. ${FRASE_SIN_API_PARA_ACEPTAR.split(". ").slice(1).join(". ")}` });
    setPaso("nada");
  };
  return (
    <div className="ap-nota" role="group" aria-label="Dar por aceptado lo que queda">
      <style>{CSS_DE_ACEPTAR}</style>
      {paso === "preguntando" ? (<>
        <p>{preguntaDeAceptarPendientes(n)}</p>
        <div className="ap-botones">
          <button className="ap-boton ap-principal" onClick={() => void aceptar()}>Sí, aceptarlos</button>
          <button className="ap-boton" onClick={() => setPaso("nada")}>No</button>
        </div>
      </>) : (<>
        <p>{fraseDeAceptarPendientes(n)}</p>
        <div className="ap-botones">
          <button className="ap-boton" disabled={paso === "aceptando"} onClick={() => setPaso("preguntando")}>{paso === "aceptando" ? "Aceptando en Word…" : rotuloDeAceptarPendientes(n)}</button>
        </div>
      </>)}
      {dicho && <p className={dicho.tono === "mal" ? "ap-mal" : "ap-bien"} role={dicho.tono === "mal" ? "alert" : "status"}>{dicho.texto}</p>}
    </div>
  );
}
